import { createApp, markRaw } from 'vue'
import Toast, { PluginOptions } from 'vue-toastification'
import { Vue3Mq } from 'vue3-mq'

import { useBookingCommerceStore } from '@/stores/bookingCommerce'
import { useBookingSelectionsStore } from '@/stores/bookingSelections'
import { useCartStore } from '@/stores/cart'
import { useCheckoutStore } from '@/stores/checkout'
import { useContentStore } from '@/stores/content'
import { useDeployVersionStore } from '@/stores/deployVersion'
import { useMainStore } from '@/stores/main'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'

import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome'
import { createGtm } from '@gtm-support/vue-gtm'
import { Ajax } from '@newrelic/browser-agent/features/ajax'
import { GenericEvents } from '@newrelic/browser-agent/features/generic_events'
import { JSErrors } from '@newrelic/browser-agent/features/jserrors'
import { SessionReplay } from '@newrelic/browser-agent/features/session_replay'
import { SessionTrace } from '@newrelic/browser-agent/features/session_trace'
import { Agent } from '@newrelic/browser-agent/loaders/agent'

import { initializeStores } from '@/composables/initializeStores'
import { resetStores } from '@/composables/resetStores'
import { initIconLibrary } from '@/lib/appIcons'
import { expireAndReset, isDataExpired, isExpirationExemptRoute } from '@/lib/dataExpiration'
import { startDeployVersionPolling } from '@/lib/deployVersion'
import { configureSnapshotStores } from '@/lib/stateSnapshot'
import { appErrorService } from '@/services/AppErrorService'

import '@/lib/devToolsAccess'
import '@VelocitySquared/cady-design-system/cady-storefront.css'
import './assets/scss/main.scss'
import 'vue-toastification/dist/index.css'
import App from './App.vue'
import router from './router'

const browserAgent = new Agent({
  loader_config: {
    accountID: import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID,
    trustKey: import.meta.env.VITE_NEW_RELIC_TRUST_KEY,
    agentID: import.meta.env.VITE_NEW_RELIC_AGENT_ID,
    licenseKey: import.meta.env.VITE_NEW_RELIC_LICENSE_KEY,
    applicationID: import.meta.env.VITE_NEW_RELIC_APPLICATION_ID
  },
  info: {
    beacon: 'bam.nr-data.net',
    errorBeacon: 'bam.nr-data.net',
    licenseKey: import.meta.env.VITE_NEW_RELIC_LICENSE_KEY,
    applicationID: import.meta.env.VITE_NEW_RELIC_APPLICATION_ID,
    sa: 1
  },
  init: {
    ajax: {
      deny_list: [
        'bam.nr-data.net',
        'google-analytics.com',
        'clarity.ms',
        'amplitude.com',
        'googleadservices.com',
        'doubleclick.net',
        'www.google.com',
        'booking.cady.com/version.json'
      ]
    },
    browser_consent_mode: {
      enabled: false
    },
    distributed_tracing: {
      enabled: true
    },
    performance: {
      capture_detail: false,
      capture_marks: false,
      capture_measures: true
    },
    privacy: {
      cookies_enabled: true
    },
    session_replay: {
      enabled: true,
      block_selector: '',
      mask_text_selector: '*',
      sampling_rate: 0.01,
      error_sampling_rate: 25.0,
      mask_all_inputs: true,
      collect_fonts: true,
      inline_images: false,
      fix_stylesheets: true,
      preload: false,
      mask_input_options: {}
    }
  },
  features: [JSErrors, Ajax, SessionTrace, SessionReplay, GenericEvents]
})

appErrorService.initialize(import.meta.env.VITE_REPORT_ERRORS_TO_NEW_RELIC === 'true', browserAgent)

initIconLibrary()

const app = createApp(App)
  .component('FontAwesomeIcon', FontAwesomeIcon)
  .component('FontAwesomeLayers', FontAwesomeLayers)

app.config.errorHandler = (err, vm, info) => {
  appErrorService.reportVueError(err, vm, info)
}

window.addEventListener('unhandledrejection', (event) => {
  appErrorService.reportUnhandledPromiseRejection(event)
})

window.onerror = function (msg, url, line, col, err) {
  appErrorService.reportWindowError(msg, url, line, col, err)
}

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
// Adds Vue Router instance to Pinia stores as a plugin
pinia.use(({ store }) => {
  store.router = markRaw(router)
})

app.use(pinia)
app.use(router)
// Install Toast before anything else can trigger a router navigation
// (or otherwise call `useToast()`). The data-expiration guard and a
// number of view bootstraps call `useToast()`, and registering it
// early means ordering changes elsewhere in this file can't strand
// callers without a toast plugin.
app.use(Toast, {} as PluginOptions)
initializeStores()

void useDeployVersionStore().captureBootVersion()
startDeployVersionPolling(router)

configureSnapshotStores(
  [
    { key: 'main', useStore: useMainStore },
    { key: 'bookingSelections', useStore: useBookingSelectionsStore },
    { key: 'cart', useStore: useCartStore },
    { key: 'checkout', useStore: useCheckoutStore },
    { key: 'bookingCommerce', useStore: useBookingCommerceStore },
    {
      key: 'content',
      useStore: useContentStore,
      omit: ['loading', 'batchLoading', 'catalogContentLoading', 'lastError']
    }
  ],
  {
    includePageData: true,
    beforeRestore: () => {
      resetStores()
    }
  }
)

// Data-expiration mutation trigger. Any `bookingSelectionsStore` mutation
// while the persisted catalog/content data is past its 12h TTL kicks off
// `expireAndReset`. Toast is installed earlier (right after the router)
// so the toast inside `expireAndReset` can render. Gated by the same lock
// + pay-flow tail exemption used by the router guard. Also bails when a
// deploy refresh is pending — the next navigation will hard-reload and
// running `expireAndReset` first would only flash a doomed
// "Refreshing your session..." overlay.
useBookingSelectionsStore().$subscribe(() => {
  if (useDeployVersionStore().deployStale) return
  const mainStore = useMainStore()
  if (mainStore.isExpiringData) return
  if (isExpirationExemptRoute(router.currentRoute.value)) return
  if (!isDataExpired(mainStore.dataLoadedAt)) return
  void expireAndReset(router)
})

app.use(Vue3Mq, {
  preset: 'bootstrap5'
})

app.use(
  createGtm({
    id: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID,
    vueRouter: router as any
  })
)

app.mount('#app')
